// frontend/src/components/notifications/NotificationChannelConfig.jsx
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import axios from '../../axiosConfig';
import notificationService from '../../services/notificationService';
import { validatePhoneFormat } from '../../utils/phoneUtils';
import { getNotificationChannelIcon } from '../../utils/notificationChannelMetadata';
import {
    Smartphone, Settings, Save, TestTube, Eye, EyeOff,
    CheckCircle, XCircle, AlertTriangle, Globe, ChevronDown
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { ThemedButton, ThemedCard, ThemedInput, ThemedSelect } from '../themed';
import ConfirmDialogShell from '../dialogs/ConfirmDialogShell';

const CUSTOM_PROVIDER_SECRET_FIELDS = {
    email: {
        smtp: ['password'],
        sendgrid: ['apiKey', 'api_key']
    },
    sms: {
        twilio: ['authToken']
    }
};

const CUSTOM_PROVIDER_REQUIRED_FIELDS = {
    email: {
        smtp: ['host', 'port', 'user', 'password'],
        sendgrid: ['apiKey', 'from_email']
    },
    sms: {
        twilio: ['accountSid', 'authToken', 'phoneNumber']
    }
};

const DEFAULT_CUSTOM_PROVIDER = {
    email: 'smtp',
    sms: 'twilio'
};

const isBlankValue = (value) => value === undefined || value === null || value === '';

const isStrictE164PhoneNumber = (value) => {
    if (isBlankValue(value) || typeof value !== 'string') {
        return false;
    }
    const validation = validatePhoneFormat(value);
    return validation.valid && value === validation.formatted;
};

const getProviderSecretFields = (channel, provider) => {
    return CUSTOM_PROVIDER_SECRET_FIELDS[channel]?.[provider] || [];
};

const getConfigFieldValue = (config, field) => {
    if (field === 'apiKey') {
        return config.apiKey ?? config.api_key;
    }
    return config[field];
};

const hasStoredCustomConfig = (channelData, originalConfig = {}) => {
    return Boolean(channelData.hasCustomConfig || originalConfig.hasCustomConfig);
};

export const buildCustomConfigUpdatePayload = (channel, provider, customConfig = {}) => {
    const secretFields = new Set(getProviderSecretFields(channel, provider));
    const payload = {};

    for (const [field, value] of Object.entries(customConfig || {})) {
        if (value === undefined || value === null) {
            continue;
        }
        // Redacted credential inputs are intentionally blank. Blank secret
        // fields mean preserve the stored credential, not overwrite it.
        if (secretFields.has(field) && value === '') {
            continue;
        }
        payload[field] = value;
    }

    return Object.keys(payload).length > 0 ? payload : undefined;
};

export const canSaveCustomProviderConfig = (channel, channelData, originalConfig = {}, defaultProvider) => {
    if (!channelData.useCustomProvider) {
        return true;
    }

    const provider = channelData.customProvider || defaultProvider;
    const originalProvider = originalConfig.customProvider;
    const providerChanged = Boolean(originalProvider && originalProvider !== provider);
    const config = channelData.customConfig || {};

    if (channel === 'sms' && provider === 'twilio') {
        const phoneNumber = getConfigFieldValue(config, 'phoneNumber');
        if (!isBlankValue(phoneNumber) && !isStrictE164PhoneNumber(phoneNumber)) {
            return false;
        }
    }

    // For an existing same-provider configuration, the backend can preserve and
    // merge against stored encrypted config. The UI does not need plaintext
    // values to save non-secret metadata or partial replacement fields.
    if (hasStoredCustomConfig(channelData, originalConfig) && !providerChanged) {
        return true;
    }

    const requiredFields = CUSTOM_PROVIDER_REQUIRED_FIELDS[channel]?.[provider] || [];
    return requiredFields.every(field => !isBlankValue(getConfigFieldValue(config, field)));
};

export const buildChannelConfigPayload = (channel, channelData, originalConfig = {}, defaultProvider) => {
    const useCustomProvider = Boolean(channelData.useCustomProvider);
    const provider = useCustomProvider ? (channelData.customProvider || defaultProvider) : null;
    const payload = {
        is_enabled: Boolean(channelData.isEnabled),
        useCustomProvider,
        customProvider: provider,
        rate_limit: channelData.rateLimit || 100
    };

    if (!useCustomProvider) {
        payload.customConfig = null;
        return payload;
    }

    const customConfigPayload = buildCustomConfigUpdatePayload(
        channel,
        provider,
        channelData.customConfig || {}
    );
    if (customConfigPayload !== undefined) {
        payload.customConfig = customConfigPayload;
    }

    return payload;
};

/**
 * Notification channel configuration component for admin users
 * Allows configuration of email, SMS, and push notification channels
 */
const NotificationChannelConfig = ({ entityId, onChannelsUpdate, manageableChannels = [] }) => {
    const { t } = useTranslation();
    const { background, text, border, cn } = useTheme();
    const currentEntity = useSelector(state => state.auth.currentEntity);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState({});
    const [testing, setTesting] = useState({});
    const [channels, setChannels] = useState([]);
    const [showPasswords, setShowPasswords] = useState({});
    const [expandedCustomConfig, setExpandedCustomConfig] = useState({});
    const [originalConfigs, setOriginalConfigs] = useState({});
    const [showDisableCustomConfigDialog, setShowDisableCustomConfigDialog] = useState(null); // null or channel name
    const [showTestDialog, setShowTestDialog] = useState(null); // null or channel name
    const [testRecipient, setTestRecipient] = useState(''); // temp storage for dialog input

    useEffect(() => {
        // Synchronize the local editable state whenever the parent sends an updated manageable channel list.
        try {
            setLoading(true);

            const normalizedChannels = (manageableChannels || []).filter(channel => channel.globallyActive);
            setChannels(normalizedChannels);

            const original = {};
            normalizedChannels.forEach(channel => {
                original[channel.channel] = {
                    useCustomProvider: channel.useCustomProvider || false,
                    customProvider: channel.customProvider,
                    customConfig: channel.customConfig ? JSON.stringify(channel.customConfig) : '',
                    hasCustomConfig: Boolean(channel.hasCustomConfig || channel.customConfig),
                    rateLimit: channel.rateLimit || 100
                };
            });

            setOriginalConfigs(original);
        } catch (error) {
            console.error('Error loading channel configuration:', error);
            toast.error(t('errorLoadingChannelConfiguration'));
        } finally {
            setLoading(false);
        }
    }, [manageableChannels, t]);

    useEffect(() => {
        // Auto-expand custom config sections for channels that already have custom settings
        const initialExpanded = {};
        channels.forEach(channel => {
            if (channel.useCustomProvider) {
                initialExpanded[channel.channel] = true;
            }
        });
        setExpandedCustomConfig(initialExpanded);
    }, [channels]);

    /**
     * Update channel configuration
     */
    const handleChannelUpdate = async (channel, config) => {
        try {
            setSaving(prev => ({ ...prev, [channel]: true }));
            
            await axios.put(`/api/notification-settings/${entityId}/channels/${channel}`, config);
            
            toast.success(t('channelConfigurationSaved'));
            notificationService.clearCacheByPattern(`menu_visibility_${entityId}`);
            notificationService.clearCacheByPattern(`entity_channels_${entityId}`);
            
            if (onChannelsUpdate) {
                onChannelsUpdate();
            }
        } catch (error) {
            console.error('Error updating channel configuration:', error);
            toast.error(t('errorUpdatingChannelConfiguration'));
        } finally {
            setSaving(prev => ({ ...prev, [channel]: false }));
        }
    };

    /**
     * Open test dialog when user clicks "Test Configuration"
     */
    const handleChannelTest = (channel) => {
        // Find the channel data to check configuration state
        const channelData = channels.find(c => c.channel === channel);

        // Prevent testing if custom configuration is not enabled
        if (channelData && !channelData.useCustomProvider) {
            toast.error(t('enableCustomConfigurationFirst'));
            return;
        }

        // Prevent testing if channel is disabled
        if (channelData && !channelData.isEnabled) {
            toast.error(t('enableChannelFirst'));
            return;
        }

        if (channel === 'sms') {
            const phoneNumber = channelData?.customConfig?.phoneNumber;
            if (!isBlankValue(phoneNumber) && !isStrictE164PhoneNumber(phoneNumber)) {
                toast.error(t('twilioPhoneNumberInvalid'));
                return;
            }
        }

        // Open test dialog
        setTestRecipient(''); // Clear previous input
        setShowTestDialog(channel);
    };

    /**
     * Actually send the test notification after user confirms in dialog
     */
    const handleConfirmTest = async () => {
        const channel = showTestDialog;
        if (!channel) return;

        // Validate test recipient
        if (!testRecipient || !testRecipient.trim()) {
            toast.error(channel === 'email' ? t('pleaseEnterTestEmailAddress') : t('pleaseEnterTestPhoneNumber'));
            return;
        }

        if (channel === 'email' && !testRecipient.includes('@')) {
            toast.error(t('pleaseEnterValidEmail'));
            return;
        }

        // Policy-aware hard block for SMS tests.
        if (channel === 'sms') {
            try {
                const checkResponse = await axios.get(`/api/notifications/${entityId}/sms/check-phone`, {
                    params: { phone: testRecipient.trim() }
                });
                const checkData = checkResponse?.data?.data;
                if (!checkData?.allowed) {
                    const policyMessage = checkData.reason === 'international_blocked'
                        ? t('smsInternationalBlocked')
                        : (checkData.reason === 'invalid_phone' ? t('invalidPhoneFormat') : t('smsDisabledBySystem'));
                    toast.error(policyMessage);
                    return;
                }
            } catch (error) {
                console.error('Error checking SMS eligibility:', error);
                toast.error(t('channelTestFailed'));
                return;
            }
        }

        // Close dialog after validation and advisory policy check
        setShowTestDialog(null);

        try {
            setTesting(prev => ({ ...prev, [channel]: true }));

            const response = await axios.post(`/api/notification-settings/${entityId}/channels/${channel}/test`, {
                testRecipient: testRecipient.trim(),
                timezone: currentEntity?.time_zone || Intl.DateTimeFormat().resolvedOptions().timeZone
            });

            if (response.data.success) {
                toast.success(response.data.message || t('channelTestSuccessful'));
            } else {
                toast.error(response.data.message || t('channelTestFailed'));
            }
        } catch (error) {
            console.error('Error testing channel:', error);
            const errorMessage = error.response?.data?.error || error.response?.data?.message || t('channelTestFailed');
            toast.error(errorMessage);
        } finally {
            setTesting(prev => ({ ...prev, [channel]: false }));
        }
    };

    /**
     * Get channel status with improved logic
     */
    const getChannelStatus = (channelData) => {
        if (channelData.is_enabled === 0 || channelData.isEnabled === 0) {
            return { icon: XCircle, color: 'text-red-500', text: t('disabled') };
        }
        
        // If channel doesn't require configuration (like push), show as ready
        if (channelData.channel === 'push') {
            return { icon: CheckCircle, color: 'text-green-500', text: t('active') };
        }
        
        // For channels that can use system defaults (like email)
        if (channelData.usingSystemDefault) {
            return { icon: Globe, color: 'text-blue-500', text: t('usingSystemDefault') };
        }
        
        // For channels with custom configuration
        if (channelData.useCustomProvider) {
            return { icon: CheckCircle, color: 'text-green-500', text: t('customized') };
        }
        
        return { icon: CheckCircle, color: 'text-green-500', text: t('active') };
    };

    /**
     * Toggle password visibility
     */
    const togglePasswordVisibility = (channel, field) => {
        setShowPasswords(prev => ({
            ...prev,
            [`${channel}_${field}`]: !prev[`${channel}_${field}`]
        }));
    };

    /**
     * Toggle custom configuration section
     */
    const toggleCustomConfig = (channel) => {
        setExpandedCustomConfig(prev => ({
            ...prev,
            [channel]: !prev[channel]
        }));
    };

    /**
     * Handle confirmation to disable custom configuration
     * Auto-saves to database immediately
     */
    const handleConfirmDisableCustomConfig = async () => {
        const channel = showDisableCustomConfigDialog;
        if (!channel) return;

        // Find the channel data
        const channelData = channels.find(c => c.channel === channel);
        if (!channelData) return;

        // Prepare config with useCustomProvider disabled
        const configToSave = {
            is_enabled: channelData.isEnabled,
            useCustomProvider: false, // Disable custom config
            customProvider: null,
            customConfig: null,
            rate_limit: channelData.rateLimit || 100
        };

        // Close dialog first
        setShowDisableCustomConfigDialog(null);

        // Turn off custom configuration in local state
        setChannels(prev => prev.map(c =>
            c.channel === channel
                ? { ...c, useCustomProvider: false }
                : c
        ));

        // Collapse the custom config section
        setExpandedCustomConfig(prev => ({ ...prev, [channel]: false }));

        // Save to database immediately
        await handleChannelUpdate(channel, configToSave);
    };

    /**
     * Check if configuration has changes from original
     */
    const hasConfigurationChanges = (channelData, channel) => {
        const original = originalConfigs[channel];
        if (!original) return false;

        const current = {
            useCustomProvider: channelData.useCustomProvider || false,
            customProvider: channelData.customProvider,
            customConfig: channelData.customConfig ? JSON.stringify(channelData.customConfig) : '',
            rateLimit: channelData.rateLimit || 100
        };

        return JSON.stringify(original) !== JSON.stringify(current);
    };

    /**
     * Render email configuration with collapsible custom config
     */
    const renderEmailConfig = (channelData) => {
        const config = channelData.customConfig || {};
        const isCustomConfigExpanded = expandedCustomConfig['email'];
        
        const handleConfigChange = (field, value) => {
            setChannels(prev => prev.map(c => 
                c.channel === 'email' 
                    ? { ...c, customConfig: { ...config, [field]: value } }
                    : c
            ));
        };

        const handleToggleCustom = () => {
            const useCustom = !channelData.useCustomProvider;

            // If turning OFF custom config and there's existing config, show warning
            if (!useCustom && channelData.useCustomProvider && (channelData.hasCustomConfig || channelData.customConfig)) {
                setShowDisableCustomConfigDialog('email');
                return;
            }

            // Otherwise, toggle directly
            setChannels(prev => prev.map(c =>
                c.channel === 'email'
                    ? { ...c, useCustomProvider: useCustom }
                    : c
            ));
            if (useCustom) {
                setExpandedCustomConfig(prev => ({ ...prev, email: true }));
            }
        };

        const handleSave = () => {
            const configToSave = buildChannelConfigPayload('email', channelData, originalConfigs.email, 'smtp');
            handleChannelUpdate('email', configToSave);
        };

        const hasChanges = hasConfigurationChanges(channelData, 'email');
        const canSaveConfig = canSaveCustomProviderConfig('email', channelData, originalConfigs.email, 'smtp');

        return (
            <div className="space-y-4">
                {/* System Default Status */}
                {channelData.usingSystemDefault && (
                    <div className={cn("p-3 rounded-lg", "bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700")}>
                        <div className="flex items-center space-x-2">
                            <Globe className="w-4 h-4 text-blue-600" />
                            <span className={cn("text-sm font-medium", "text-blue-800 dark:text-blue-200")}>
                                {t('usingSystemDefault')}
                            </span>
                        </div>
                        <p className={cn("text-xs mt-1", "text-blue-600 dark:text-blue-300")}>
                            {t('emailCanUseSystemConfiguration')}
                        </p>
                    </div>
                )}

                {/* Custom Configuration Toggle */}
                <div className="flex items-center justify-between">
                    <div>
                        <h4 className={cn("font-medium", text.primary)}>{t('customConfiguration')}</h4>
                        <p className={cn("text-sm", text.secondary)}>{t('overrideSystemDefaults')}</p>
                    </div>
                    <button
                        onClick={handleToggleCustom}
                        className={cn(
                            "w-12 h-6 rounded-full transition-colors relative",
                            channelData.useCustomProvider 
                                ? "bg-blue-500" 
                                : "bg-gray-300 dark:bg-gray-600"
                        )}
                    >
                        <div className={cn(
                            "w-4 h-4 rounded-full bg-white transition-transform absolute top-1",
                            channelData.useCustomProvider ? "translate-x-7" : "translate-x-1"
                        )} />
                    </button>
                </div>

                {/* Show/Hide Custom Config Button */}
                {channelData.useCustomProvider && (
                    <ThemedButton
                        onClick={() => toggleCustomConfig('email')}
                        variant="outline"
                        size="sm"
                        className="w-full"
                    >
                        <Settings className="w-4 h-4 mr-2" />
                        {isCustomConfigExpanded ? t('hideCustomConfiguration') : t('showCustomConfiguration')}
                        <ChevronDown className={cn(
                            "w-4 h-4 ml-2 transition-transform",
                            isCustomConfigExpanded ? "rotate-180" : ""
                        )} />
                    </ThemedButton>
                )}

                {/* Collapsible Custom Configuration */}
                {channelData.useCustomProvider && isCustomConfigExpanded && (
                    <div className={cn("space-y-4 p-4 rounded-lg border", border.primary, background.secondary)}>
                        {/* Provider Selection */}
                        <div>
                            <label className={cn("block text-sm font-medium mb-2", text.primary)}>
                                {t('emailProvider')}
                            </label>
                            <ThemedSelect
                                value={channelData.customProvider || 'smtp'}
                                onChange={(e) => {
                                    setChannels(prev => prev.map(c => 
                                        c.channel === 'email' 
                                            ? { ...c, customProvider: e.target.value, customConfig: {} }
                                            : c
                                    ));
                                }}
                            >
                                <option value="smtp">{t('smtp')}</option>
                                <option value="sendgrid">{t('sendGrid')}</option>
                            </ThemedSelect>
                        </div>

                        {/* SMTP Configuration */}
                        {(channelData.customProvider || 'smtp') === 'smtp' && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className={cn("block text-sm font-medium mb-2", text.primary)}>
                                        {t('smtpHost')}
                                    </label>
                                    <ThemedInput
                                        type="text"
                                        value={config.host || ''}
                                        onChange={(e) => handleConfigChange('host', e.target.value)}
                                        placeholder="smtp.gmail.com"
                                    />
                                </div>
                                <div>
                                    <label className={cn("block text-sm font-medium mb-2", text.primary)}>
                                        {t('smtpPort')}
                                    </label>
                                    <ThemedInput
                                        type="number"
                                        value={config.port || ''}
                                        onChange={(e) => handleConfigChange('port', e.target.value)}
                                        placeholder="587"
                                    />
                                </div>
                                <div>
                                    <label className={cn("block text-sm font-medium mb-2", text.primary)}>
                                        {t('smtpUsername')}
                                    </label>
                                    <ThemedInput
                                        type="text"
                                        value={config.user || ''}
                                        onChange={(e) => handleConfigChange('user', e.target.value)}
                                        placeholder="your-email@gmail.com"
                                    />
                                </div>
                                <div>
                                    <label className={cn("block text-sm font-medium mb-2", text.primary)}>
                                        {t('smtpPassword')}
                                    </label>
                                    <div className="relative">
                                        <ThemedInput
                                            type={showPasswords.email_password ? "text" : "password"}
                                            value={config.password || ''}
                                            onChange={(e) => handleConfigChange('password', e.target.value)}
                                            placeholder="••••••••"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => togglePasswordVisibility('email', 'password')}
                                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                        >
                                            {showPasswords.email_password ? 
                                                <EyeOff className="h-4 w-4 text-gray-400" /> : 
                                                <Eye className="h-4 w-4 text-gray-400" />
                                            }
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <label className={cn("block text-sm font-medium mb-2", text.primary)}>
                                        {t('fromEmail')}
                                    </label>
                                    <ThemedInput
                                        type="email"
                                        value={config.from_email || ''}
                                        onChange={(e) => handleConfigChange('from_email', e.target.value)}
                                        placeholder="noreply@yourdomain.com"
                                    />
                                    <p className={cn("text-xs mt-1", text.secondary)}>
                                        {t('fromEmailHelperText')}
                                    </p>
                                </div>
                                <div>
                                    <label className={cn("block text-sm font-medium mb-2", text.primary)}>
                                        {t('fromName')} <span className="text-gray-400">({t('optional')})</span>
                                    </label>
                                    <ThemedInput
                                        type="text"
                                        value={config.from_name || ''}
                                        onChange={(e) => handleConfigChange('from_name', e.target.value)}
                                        placeholder="Your Company Name"
                                    />
                                    <p className={cn("text-xs mt-1", text.secondary)}>
                                        {t('fromNameHelperText')}
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* SendGrid Configuration */}
                        {channelData.customProvider === 'sendgrid' && (
                            <div>
                                <label className={cn("block text-sm font-medium mb-2", text.primary)}>
                                    {t('sendGridApiKey')}
                                </label>
                                <div className="relative">
                                        <ThemedInput
                                            type={showPasswords.email_apikey ? "text" : "password"}
                                            value={config.apiKey || ''}
                                            onChange={(e) => handleConfigChange('apiKey', e.target.value)}
                                        placeholder="SG.••••••••"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => togglePasswordVisibility('email', 'apikey')}
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    >
                                        {showPasswords.email_apikey ? 
                                            <EyeOff className="h-4 w-4 text-gray-400" /> : 
                                            <Eye className="h-4 w-4 text-gray-400" />
                                        }
                                    </button>
                                </div>
                                <div className="mt-4">
                                    <label className={cn("block text-sm font-medium mb-2", text.primary)}>
                                        {t('fromEmail')}
                                    </label>
                                    <ThemedInput
                                        type="email"
                                        value={config.from_email || ''}
                                        onChange={(e) => handleConfigChange('from_email', e.target.value)}
                                        placeholder="noreply@yourdomain.com"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Rate Limit */}
                        <div>
                            <label className={cn("block text-sm font-medium mb-2", text.primary)}>
                                {t('rateLimitPerHour')}
                            </label>
                            <ThemedInput
                                type="number"
                                value={channelData.rateLimit || 100}
                                onChange={(e) => {
                                    setChannels(prev => prev.map(c =>
                                        c.channel === 'email'
                                            ? { ...c, rateLimit: parseInt(e.target.value) }
                                            : c
                                    ));
                                }}
                                min="1"
                                max="1000"
                            />
                        </div>
                    </div>
                )}

                {/* Action Buttons */}
                <div className="flex space-x-3 pt-4">
                    <ThemedButton
                        onClick={handleSave}
                        disabled={saving.email || !channelData.useCustomProvider || !hasChanges || !canSaveConfig}
                    >
                        <Save className="w-4 h-4 mr-1" />
                        {saving.email ? t('saving') : t('saveConfiguration')}
                    </ThemedButton>
                    <ThemedButton
                        onClick={() => handleChannelTest('email')}
                        variant="outline"
                        disabled={!channelData.isEnabled || testing.email || !channelData.useCustomProvider}
                    >
                        <TestTube className="w-4 h-4 mr-1" />
                        {testing.email ? t('testing') : t('testConfiguration')}
                    </ThemedButton>
                </div>
            </div>
        );
    };

    /**
     * Render SMS configuration with collapsible custom config
     */
    const renderSMSConfig = (channelData) => {
        const config = channelData.customConfig || {};
        const isCustomConfigExpanded = expandedCustomConfig['sms'];
        
        const handleConfigChange = (field, value) => {
            setChannels(prev => prev.map(c => 
                c.channel === 'sms' 
                    ? { ...c, customConfig: { ...config, [field]: value } }
                    : c
            ));
        };

        const handleToggleCustom = () => {
            const useCustom = !channelData.useCustomProvider;

            // If turning OFF custom config and there's existing config, show warning
            if (!useCustom && channelData.useCustomProvider && (channelData.hasCustomConfig || channelData.customConfig)) {
                setShowDisableCustomConfigDialog('sms');
                return;
            }

            // Otherwise, toggle directly
            setChannels(prev => prev.map(c =>
                c.channel === 'sms'
                    ? { ...c, useCustomProvider: useCustom }
                    : c
            ));
            if (useCustom) {
                setExpandedCustomConfig(prev => ({ ...prev, sms: true }));
            }
        };

        const handleSave = () => {
            const configToSave = {
                ...buildChannelConfigPayload('sms', channelData, originalConfigs.sms, 'twilio'),
                // Include destination_policy for SMS channel (entity admin controls)
                destinationPolicy: channelData.destinationPolicy || 'us_ca'
            };
            handleChannelUpdate('sms', configToSave);
        };

        const hasChanges = hasConfigurationChanges(channelData, 'sms');
        const canSaveConfig = canSaveCustomProviderConfig('sms', channelData, originalConfigs.sms, 'twilio');
        const twilioPhoneNumber = config.phoneNumber || '';
        const twilioPhoneNumberInvalid = !isBlankValue(twilioPhoneNumber) && !isStrictE164PhoneNumber(twilioPhoneNumber);

        return (
            <div className="space-y-4">
                {/* System Default Status */}
                {channelData.usingSystemDefault && (
                    <div className={cn("p-3 rounded-lg", "bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700")}>
                        <div className="flex items-center space-x-2">
                            <Globe className="w-4 h-4 text-blue-600" />
                            <span className={cn("text-sm font-medium", "text-blue-800 dark:text-blue-200")}>
                                {t('usingSystemDefault')}
                            </span>
                        </div>
                        <p className={cn("text-xs mt-1", "text-blue-600 dark:text-blue-300")}>
                            {t('smsCanUseSystemConfiguration')}
                        </p>
                    </div>
                )}

                {/* Custom Configuration Toggle */}
                <div className="flex items-center justify-between">
                    <div>
                        <h4 className={cn("font-medium", text.primary)}>{t('customConfiguration')}</h4>
                        <p className={cn("text-sm", text.secondary)}>{t('overrideSystemDefaults')}</p>
                    </div>
                    <button
                        onClick={handleToggleCustom}
                        className={cn(
                            "w-12 h-6 rounded-full transition-colors relative",
                            channelData.useCustomProvider 
                                ? "bg-blue-500" 
                                : "bg-gray-300 dark:bg-gray-600"
                        )}
                    >
                        <div className={cn(
                            "w-4 h-4 rounded-full bg-white transition-transform absolute top-1",
                            channelData.useCustomProvider ? "translate-x-7" : "translate-x-1"
                        )} />
                    </button>
                </div>

                {/* Show/Hide Custom Config Button */}
                {channelData.useCustomProvider && (
                    <ThemedButton
                        onClick={() => toggleCustomConfig('sms')}
                        variant="outline"
                        size="sm"
                        className="w-full"
                    >
                        <Settings className="w-4 h-4 mr-2" />
                        {isCustomConfigExpanded ? t('hideCustomConfiguration') : t('showCustomConfiguration')}
                        <ChevronDown className={cn(
                            "w-4 h-4 ml-2 transition-transform",
                            isCustomConfigExpanded ? "rotate-180" : ""
                        )} />
                    </ThemedButton>
                )}

                {/* Collapsible Custom Configuration */}
                {channelData.useCustomProvider && isCustomConfigExpanded && (
                    <div className={cn("space-y-4 p-4 rounded-lg border", border.primary, background.secondary)}>
                        {/* Provider Selection */}
                        <div>
                            <label className={cn("block text-sm font-medium mb-2", text.primary)}>
                                {t('smsProvider')}
                            </label>
                            <ThemedSelect
                                value={channelData.customProvider || 'twilio'}
                                onChange={(e) => {
                                    setChannels(prev => prev.map(c => 
                                        c.channel === 'sms' 
                                            ? { ...c, customProvider: e.target.value }
                                            : c
                                    ));
                                }}
                            >
                                <option value="twilio">{t('twilio')}</option>
                            </ThemedSelect>
                        </div>

                        {/* Twilio Configuration */}
                        {(channelData.customProvider || 'twilio') === 'twilio' && (
                            <div className="grid grid-cols-1 gap-4">
                                <div>
                                    <label className={cn("block text-sm font-medium mb-2", text.primary)}>
                                        {t('twilioAccountSid')}
                                    </label>
                                    <ThemedInput
                                        type="text"
                                        value={config.accountSid || ''}
                                        onChange={(e) => handleConfigChange('accountSid', e.target.value)}
                                        placeholder="AC••••••••"
                                    />
                                </div>
                                <div>
                                    <label className={cn("block text-sm font-medium mb-2", text.primary)}>
                                        {t('twilioAuthToken')}
                                    </label>
                                    <div className="relative">
                                        <ThemedInput
                                            type={showPasswords.sms_token ? "text" : "password"}
                                            value={config.authToken || ''}
                                            onChange={(e) => handleConfigChange('authToken', e.target.value)}
                                            placeholder="••••••••"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => togglePasswordVisibility('sms', 'token')}
                                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                        >
                                            {showPasswords.sms_token ? 
                                                <EyeOff className="h-4 w-4 text-gray-400" /> : 
                                                <Eye className="h-4 w-4 text-gray-400" />
                                            }
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <label className={cn("block text-sm font-medium mb-2", text.primary)}>
                                        {t('twilioPhoneNumber')}
                                    </label>
                                    <ThemedInput
                                        type="text"
                                        value={config.phoneNumber || ''}
                                        onChange={(e) => handleConfigChange('phoneNumber', e.target.value)}
                                        placeholder="+1234567890"
                                        aria-invalid={twilioPhoneNumberInvalid}
                                    />
                                    <p className={cn(
                                        "text-xs mt-1",
                                        twilioPhoneNumberInvalid ? "text-red-600 dark:text-red-400" : text.secondary
                                    )}>
                                        {twilioPhoneNumberInvalid ? t('twilioPhoneNumberInvalid') : t('twilioPhoneNumberHelperText')}
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Rate Limit */}
                        <div>
                            <label className={cn("block text-sm font-medium mb-2", text.primary)}>
                                {t('rateLimitPerHour')}
                            </label>
                            <ThemedInput
                                type="number"
                                value={channelData.rateLimit || 100}
                                onChange={(e) => {
                                    setChannels(prev => prev.map(c =>
                                        c.channel === 'sms'
                                            ? { ...c, rateLimit: parseInt(e.target.value) }
                                            : c
                                    ));
                                }}
                                min="1"
                                max="1000"
                            />
                        </div>

                        {/* SMS Destinations — entity admin controls which numbers can receive SMS */}
                        <div>
                            <label className={cn("block text-sm font-medium mb-2", text.primary)}>
                                {t('smsDestinationPolicy')}
                            </label>
                            <select
                                value={channelData.destinationPolicy || 'us_ca'}
                                onChange={(e) => {
                                    setChannels(prev => prev.map(c =>
                                        c.channel === 'sms'
                                            ? { ...c, destinationPolicy: e.target.value }
                                            : c
                                    ));
                                }}
                                className={cn(
                                    "w-full p-2 border rounded-md text-sm",
                                    "border-gray-300 dark:border-gray-600",
                                    "bg-white dark:bg-gray-700",
                                    text.primary
                                )}
                            >
                                <option value="us_ca">{t('smsPolicyUsCa')}</option>
                                <option value="all">{t('smsPolicyAll')}</option>
                            </select>
                            <p className={cn("text-xs mt-1", text.secondary)}>
                                {t('smsDestinationPolicyHelp')}
                            </p>
                            {/* Cost warning when 'all' is selected */}
                            {(channelData.destinationPolicy === 'all') && (
                                <div className="mt-1 flex items-start gap-1.5 text-xs text-amber-600 dark:text-amber-400">
                                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                                    <span>{t('smsInternationalCostWarning')}</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Action Buttons */}
                <div className="flex space-x-3 pt-4">
                    <ThemedButton
                        onClick={handleSave}
                        disabled={saving.sms || !channelData.useCustomProvider || !hasChanges || !canSaveConfig}
                    >
                        <Save className="w-4 h-4 mr-1" />
                        {saving.sms ? t('saving') : t('saveConfiguration')}
                    </ThemedButton>
                    <ThemedButton
                        onClick={() => handleChannelTest('sms')}
                        variant="outline"
                        disabled={!channelData.isEnabled || testing.sms || !channelData.useCustomProvider || !canSaveConfig}
                    >
                        <TestTube className="w-4 h-4 mr-1" />
                        {testing.sms ? t('testing') : t('testConfiguration')}
                    </ThemedButton>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className={cn("flex justify-center items-center h-32", text.secondary)}>
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                <span className="ml-3">{t('loadingConfiguration')}...</span>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div>
                <h2 className={cn("text-lg font-semibold", text.primary)}>
                    {t('notificationChannelConfiguration')}
                </h2>
                <p className={cn("text-sm", text.secondary)}>
                    {t('configureChannelsDescription')}
                </p>
            </div>

            {channels.map(channelData => {
                const Icon = getNotificationChannelIcon(channelData.channel, Settings);
                const status = getChannelStatus(channelData);
                const StatusIcon = status.icon;

                return (
                    <ThemedCard key={channelData.channel} className="p-6">
                        {/* Channel Header */}
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center space-x-3">
                                <Icon className="w-6 h-6" />
                                <div>
                                    <h3 className={cn("text-lg font-medium", text.primary)}>
                                        {t(`${channelData.channel}Notifications`)}
                                    </h3>
                                    <div className="flex items-center space-x-2">
                                        <StatusIcon className={cn("w-4 h-4", status.color)} />
                                        <span className={cn("text-sm", status.color)}>
                                            {status.text}
                                        </span>
                                        {!channelData.globallyAvailable && (
                                            <span className={cn("text-xs px-2 py-1 rounded", "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300")}>
                                                <Globe className="w-3 h-3 inline mr-1" />
                                                {t('contactSystemAdmin')}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            
                            {channelData.globallyAvailable && (
                                <button
                                    onClick={() => {
                                        const updatedConfig = buildChannelConfigPayload(
                                            channelData.channel,
                                            { ...channelData, isEnabled: !channelData.isEnabled },
                                            originalConfigs[channelData.channel],
                                            DEFAULT_CUSTOM_PROVIDER[channelData.channel]
                                        );
                                        if (channelData.channel === 'sms') {
                                            updatedConfig.destinationPolicy = channelData.destinationPolicy || 'us_ca';
                                        }
                                        handleChannelUpdate(channelData.channel, updatedConfig);
                                    }}
                                    className={cn(
                                        "w-12 h-6 rounded-full transition-colors relative",
                                        channelData.isEnabled 
                                            ? "bg-green-500" 
                                            : "bg-gray-300 dark:bg-gray-600"
                                    )}
                                    disabled={saving[channelData.channel]}
                                >
                                    <div className={cn(
                                        "w-4 h-4 rounded-full bg-white transition-transform absolute top-1",
                                        channelData.isEnabled ? "translate-x-7" : "translate-x-1"
                                    )} />
                                </button>
                            )}
                        </div>

                        {/* Channel Configuration */}
                        {channelData.globallyAvailable && channelData.isEnabled ? (
                            <>
                                {channelData.channel === 'email' && renderEmailConfig(channelData)}
                                {channelData.channel === 'sms' && renderSMSConfig(channelData)}
                                {channelData.channel === 'push' && (
                                    <div className="text-center py-8">
                                        <Smartphone className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                                        <p className={cn("text-sm", text.secondary)}>
                                            {t('pushNotificationsAutoConfigured')}
                                        </p>
                                    </div>
                                )}
                            </>
                        ) : null}
                    </ThemedCard>
                );
            })}

            {/* Confirm Disable Custom Config Dialog */}
            <ConfirmDialogShell
                isOpen={!!showDisableCustomConfigDialog}
                onClose={() => setShowDisableCustomConfigDialog(null)}
                onConfirm={handleConfirmDisableCustomConfig}
                title={t('disableCustomConfiguration')}
                confirmLabel={t('disableCustomConfig')}
                cancelLabel={t('cancel')}
                confirmVariant="danger"
                showIcon={true}
                iconVariant="danger"
                confirmIcon={AlertTriangle}
            >
                <div className="space-y-3">
                    <p className={cn("text-sm", text.primary)}>
                        {t('disableCustomConfigWarning', {
                            channel: showDisableCustomConfigDialog === 'email' ? t('email') : t('sms')
                        })}
                    </p>
                    <div className={cn("p-3 rounded-lg", "bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700")}>
                        <p className={cn("text-xs font-medium", "text-red-800 dark:text-red-200")}>
                            {t('customConfigWillBeDeleted')}
                        </p>
                    </div>
                    <p className={cn("text-sm font-medium", text.primary)}>
                        {t('confirmDisableCustomConfig')}
                    </p>
                </div>
            </ConfirmDialogShell>

            {/* Test Configuration Dialog */}
            <ConfirmDialogShell
                isOpen={!!showTestDialog}
                onClose={() => setShowTestDialog(null)}
                onConfirm={handleConfirmTest}
                title={t('testConfiguration')}
                confirmLabel={t('sendTest')}
                cancelLabel={t('cancel')}
                confirmVariant="primary"
                showIcon={true}
                iconVariant="info"
                confirmIcon={TestTube}
            >
                <div className="space-y-3">
                    <p className={cn("text-sm", text.primary)}>
                        {showTestDialog === 'email'
                            ? t('enterTestEmailAddress')
                            : t('enterTestPhoneNumber')
                        }
                    </p>
                    <div>
                        <label className={cn("block text-sm font-medium mb-2", text.primary)}>
                            {showTestDialog === 'email' ? t('emailAddress') : t('phoneNumber')}
                        </label>
                        <ThemedInput
                            type={showTestDialog === 'email' ? 'email' : 'tel'}
                            value={testRecipient}
                            onChange={(e) => setTestRecipient(e.target.value)}
                            placeholder={showTestDialog === 'email' ? 'test@example.com' : '+1234567890'}
                            autoFocus
                        />
                    </div>
                </div>
            </ConfirmDialogShell>
        </div>
    );
};

export default NotificationChannelConfig;
